import React, { useState, useEffect } from "react";
import apiEndpoints from "@store/apiEndpoints";
import errorFunctions from "@store/errorFunctions";
import { useToast } from "@components/Toast/ToastContext";
import Modal from "react-modal";
import Select from "react-select";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import {
  MdClose,
  MdCheckBoxOutlineBlank,
  MdCheckBox,
} from "react-icons/md";

const Cin7ImportModal = ({
  isOpen,
  onClose,
  brands,
  protectedFetch,
  onImportSuccess,
}) => {
  const { showToast } = useToast();
  const [loadingExisting, setLoadingExisting] = useState(false);
  const [existingSkus, setExistingSkus] = useState(new Set());
  const [selectedBrand, setSelectedBrand] = useState(null);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [results, setResults] = useState([]);
  const [selected, setSelected] = useState({});
  const [importing, setImporting] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  // Reset everything each time the modal opens, back to the brand-picker step
  useEffect(() => {
    if (!isOpen) return;
    setSelectedBrand(null);
    setResults([]);
    setSelected({});
    setExistingSkus(new Set());
    setSearchQuery("");
  }, [isOpen]);

  // Once a brand is picked: fetch its existing devices (to know which SKUs
  // already exist for it) and its CIN7 products (via its sku_code), in parallel
  useEffect(() => {
    if (!selectedBrand) return;

    setLoadingExisting(true);
    protectedFetch(
      apiEndpoints.device.listByBrand(selectedBrand.id),
      "GET",
      null,
      errorFunctions.checkResponseThenJson,
    )
      .then((res) => {
        const devices = Array.isArray(res) ? res : res?.results || [];
        setExistingSkus(new Set(devices.map((d) => d.sku)));
      })
      .catch(() => {
        showToast("Failed to load existing devices", "error");
      })
      .finally(() => {
        setLoadingExisting(false);
      });

    setLoadingProducts(true);
    protectedFetch(
      apiEndpoints.brand.detail(selectedBrand.id),
      "GET",
      null,
      errorFunctions.checkResponseThenJson,
    )
      .then((res) => {
        const skuCode = res?.sku_code;
        if (!skuCode) {
          showToast("This brand has no SKU code configured", "warning");
          setResults([]);
          return null;
        }
        return protectedFetch(
          apiEndpoints.device.listDeviceCin7(skuCode),
          "GET",
          null,
          errorFunctions.checkResponseThenJson,
        );
      })
      .then((res) => {
        if (!res) return;
        const data = Array.isArray(res) ? res : res?.results || res?.data || [];
        setResults(Array.isArray(data) ? data : []);
      })
      .catch(() => {
        showToast("Failed to fetch CIN7 products", "error");
        setResults([]);
      })
      .finally(() => {
        setLoadingProducts(false);
      });
  }, [selectedBrand]);

  // Auto-select every product that isn't already in this brand's device list
  useEffect(() => {
    if (results.length === 0) {
      setSelected({});
      return;
    }
    const autoSelected = {};
    results.forEach((item, i) => {
      if (!existingSkus.has(item.sku)) autoSelected[i] = true;
    });
    setSelected(autoSelected);
  }, [results, existingSkus]);

  const handleChangeBrand = () => {
    setSelectedBrand(null);
    setResults([]);
    setSelected({});
    setSearchQuery("");
  };

  const newResults = results.filter((item) => !existingSkus.has(item.sku));

  const filteredResults = results
    .map((item, idx) => ({ item, idx }))
    .filter(({ item }) => {
      const q = searchQuery.trim().toLowerCase();
      if (!q) return true;
      const sku = (item.sku || "").toLowerCase();
      const name = (item.name || "").toLowerCase();
      const category = (item.category_name || item.category || "").toLowerCase();
      return sku.includes(q) || name.includes(q) || category.includes(q);
    });

  const toggleSelectAll = () => {
    if (newResults.length === 0) return;
    const newIndices = results
      .map((item, i) => (!existingSkus.has(item.sku) ? i : -1))
      .filter((i) => i !== -1);
    const allNewSelected = newIndices.every((i) => selected[i]);
    if (allNewSelected) {
      setSelected({});
    } else {
      const newSelected = {};
      newIndices.forEach((i) => {
        newSelected[i] = true;
      });
      setSelected(newSelected);
    }
  };

  const toggleSelectItem = (index) => {
    setSelected((prev) => ({ ...prev, [index]: !prev[index] }));
  };

  const selectedCount = Object.values(selected).filter(Boolean).length;

  const newItems = results.filter(
    (_, i) => selected[i] && !existingSkus.has(results[i].sku),
  );

  const handleImport = () => {
    if (newItems.length === 0 || importing) return;

    setImporting(true);

    const payload = newItems.map((item) => ({
      brand_id: parseInt(selectedBrand.id, 10) || 0,
      name: item.name || "",
      weight: item.weight ?? 0,
      height: item.height ?? 0,
      width: item.width ?? 0,
      length: item.length ?? 0,
      description: item.description || "",
      sku: item.sku || "",
      shipping_vendor: "auspost",
    }));

    protectedFetch(apiEndpoints.device.bulk(), "POST", payload, errorFunctions.checkResponseThenJson)
      .then(() => {
        showToast(`${newItems.length} device(s) imported successfully`, "success");
        onClose();
        onImportSuccess();
        setImporting(false);
      })
      .catch(() => {
        showToast("Import failed. Please try again.", "error");
        setImporting(false);
      });
  };

  const loading = loadingExisting || loadingProducts;

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={onClose}
      className={"card"}
      style={{
        overlay: { zIndex: 1050, background: "rgba(0,0,0,0.5)" },
        content: {
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: "90%",
          maxWidth: 700,
          maxHeight: "85vh",
          margin: 0,
          padding: 0,
          border: "none",
          borderRadius: 8,
          overflow: "hidden",
        },
      }}
    >
      <div
        className="card-header"
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <span style={{ fontWeight: 700, fontSize: "1rem" }}>
          Import from CIN7
        </span>
        <button onClick={onClose} className={"info-modal-close"}>
          <MdClose />
        </button>
      </div>

      <div className="card-body">
        {!selectedBrand ? (
          <div style={{ padding: "8px 0" }}>
            <label
              className={"single-warranty-details-label"}
              style={{ display: "block", marginBottom: 8 }}
            >
              Select Brand
            </label>
            <Select
              options={(brands || []).map((brand) => ({
                value: brand.id,
                label: brand.name,
              }))}
              onChange={(option) => {
                const brand = (brands || []).find((b) => b.id === option?.value);
                setSelectedBrand(brand || null);
              }}
              placeholder="Select Brand"
              value={null}
              menuPortalTarget={document.body}
              styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
            />
          </div>
        ) : (
          <>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                marginBottom: 12,
              }}
            >
              <span style={{ fontWeight: 600, fontSize: "0.9rem" }}>
                {selectedBrand.name}
              </span>
              <button
                className="btn btn-outline-secondary btn-sm"
                onClick={handleChangeBrand}
                style={{ borderRadius: 6, fontSize: "0.8rem" }}
              >
                Change brand
              </button>
            </div>

            {loading ? (
              <div style={{ textAlign: "center", padding: 32 }}>
                <div className="spinner-border text-primary" role="status" />
              </div>
            ) : results.length > 0 ? (
              <>
                <input
                  type="text"
                  className="form-control form-control-sm"
                  placeholder="Search by SKU, name, or category..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  style={{ borderRadius: 8, fontSize: "0.85rem", marginBottom: 8 }}
                />

                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    marginBottom: 8,
                  }}
                >
                  <button
                    className="btn btn-outline-secondary btn-sm"
                    onClick={toggleSelectAll}
                    disabled={newResults.length === 0}
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 6,
                      borderRadius: 6,
                      fontSize: "0.8rem",
                      padding: "4px 12px",
                    }}
                  >
                    {newResults.length > 0 &&
                    newResults.every((item) => {
                      const idx = results.indexOf(item);
                      return selected[idx];
                    }) ? (
                      <MdCheckBox size={18} />
                    ) : (
                      <MdCheckBoxOutlineBlank size={18} />
                    )}
                    {newResults.length > 0 &&
                    newResults.every((item) => {
                      const idx = results.indexOf(item);
                      return selected[idx];
                    })
                      ? "Deselect All"
                      : "Select All"}
                  </button>
                  {selectedCount > 0 && (
                    <span
                      style={{
                        fontSize: "0.8rem",
                        color: "#6b7280",
                        fontWeight: 500,
                      }}
                    >
                      {selectedCount} selected
                    </span>
                  )}
                </div>

                {filteredResults.length === 0 && (
                  <div style={{ textAlign: "center", padding: "16px 0" }}>
                    <p style={{ color: "#9ca3af", fontSize: "0.85rem", margin: 0 }}>
                      No products match your search
                    </p>
                  </div>
                )}

                {filteredResults.length > 0 && (
                  <TableContainer component={Paper} style={{ maxHeight: 320 }}>
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow style={{ background: "#f9fafb" }}>
                          <TableCell style={{ fontWeight: 700, width: 50 }}>
                            #
                          </TableCell>
                          <TableCell style={{ fontWeight: 700 }}>SKU</TableCell>
                          <TableCell style={{ fontWeight: 700 }}>Name</TableCell>
                          <TableCell style={{ fontWeight: 700 }}>Category</TableCell>
                          <TableCell style={{ fontWeight: 700 }}>Status</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {filteredResults.map(({ item, idx }) => {
                          const isExisting = existingSkus.has(item.sku);
                          return (
                            <TableRow
                              key={idx}
                              hover={!isExisting}
                              onClick={() => !isExisting && toggleSelectItem(idx)}
                              style={{
                                cursor: isExisting ? "default" : "pointer",
                                background: selected[idx]
                                  ? "#eef2ff"
                                  : isExisting
                                    ? "#f9fafb"
                                    : undefined,
                                opacity: isExisting ? 0.6 : 1,
                              }}
                            >
                              <TableCell>
                                {isExisting ? (
                                  <MdCheckBoxOutlineBlank size={18} color="#d1d5db" />
                                ) : selected[idx] ? (
                                  <MdCheckBox size={18} color="#4f46e5" />
                                ) : (
                                  <MdCheckBoxOutlineBlank size={18} color="#9ca3af" />
                                )}
                              </TableCell>
                              <TableCell
                                style={{
                                  fontFamily: "monospace",
                                  fontSize: "0.85rem",
                                }}
                              >
                                {item.sku || "—"}
                              </TableCell>
                              <TableCell>{item.name || "—"}</TableCell>
                              <TableCell>
                                {item?.category_name || item?.category || "—"}
                              </TableCell>
                              <TableCell>
                                {isExisting ? (
                                  <span
                                    style={{
                                      fontSize: "0.75rem",
                                      color: "#6b7280",
                                      fontWeight: 500,
                                    }}
                                  >
                                    Already in brand
                                  </span>
                                ) : (
                                  <span
                                    style={{
                                      fontSize: "0.75rem",
                                      color: "#059669",
                                      fontWeight: 500,
                                    }}
                                  >
                                    New
                                  </span>
                                )}
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </>
            ) : (
              <div style={{ textAlign: "center", padding: "32px 16px" }}>
                <p style={{ color: "#6b7280", fontWeight: 500, marginBottom: 4 }}>
                  No products found
                </p>
                <p style={{ color: "#9ca3af", fontSize: "0.85rem" }}>
                  No CIN7 products found for this brand
                </p>
              </div>
            )}
          </>
        )}
      </div>

      {selectedBrand && !loading && results.length > 0 && (
        <div
          className="card-footer"
          style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}
        >
          <button
            className="btn btn-outline-secondary btn-sm"
            onClick={onClose}
            style={{ borderRadius: 8, fontWeight: 500, padding: "6px 20px" }}
          >
            Cancel
          </button>
          <button
            className="btn btn-primary btn-sm"
            onClick={handleImport}
            disabled={newItems.length === 0 || importing}
            style={{
              borderRadius: 8,
              fontWeight: 600,
              padding: "6px 20px",
              display: "inline-flex",
              alignItems: "center",
              gap: 4,
            }}
          >
            {importing ? (
              <>
                <span
                  className="spinner-border spinner-border-sm"
                  role="status"
                  style={{ width: 14, height: 14 }}
                />{" "}
                Importing...
              </>
            ) : (
              <>
                Import Selected{newItems.length > 0 ? ` (${newItems.length})` : ""}
              </>
            )}
          </button>
        </div>
      )}
    </Modal>
  );
};

export default Cin7ImportModal;
